import React, { useMemo } from 'react';
import { Folder, Prompt, SortPreferences, SortOption } from './types';
import { storage } from './utils/storage';
import { checkFeatureEnabled } from './utils/features';
import { useHybridTheme } from './themes/useHybridTheme';
import { STORAGE_KEYS } from './constants/storageKeys';
import { BackupManager } from './utils/backupManager';
import { deleteFolderWithPrompts } from './utils/folderDeletion';
import { getPromptService } from './infrastructure';
import { getPlatform, StorageChangeMap } from './infrastructure/platform';
import Sidebar from './components/Sidebar';
import PromptList from './components/PromptList';
import Toast from './components/Toast';
import RatingPrompt from './components/RatingPrompt';
import { recordPromptUsage } from './utils/ratingPrompt';
import PromptForm from './components/PromptForm';
import { Dialog } from './components/Dialog';
import { FolderEditDialog } from './components/FolderEditDialog';
const isExtension = getPlatform().capabilities.persistentStorage;

const SPECIAL_FOLDERS = {
  ALL: { id: 'all', name: 'All Prompts', color: '#6366F1', isSpecial: true },
  UNSORTED: { id: 'unsorted', name: 'Unsorted Prompts', color: '#9CA3AF', isSpecial: true },
} as const;

function FullscreenApp() {
  const { getClasses, getColor } = useHybridTheme();
  const [folders, setFolders] = React.useState<Folder[]>([]);
  const [prompts, setPrompts] = React.useState<Prompt[]>([]);
  const [selectedFolderId, setSelectedFolderId] = React.useState<string | null>(null);
  const [showDeleteFolderDialog, setShowDeleteFolderDialog] = React.useState(false);
  const [showRenameFolderDialog, setShowRenameFolderDialog] = React.useState(false);
  const [showDeletePromptDialog, setShowDeletePromptDialog] = React.useState(false);
  const [dialogFolderId, setDialogFolderId] = React.useState<string | null>(null);
  const [dialogPromptId, setDialogPromptId] = React.useState<string | null>(null);
  const [rememberLastFolderEnabled, setRememberLastFolderEnabled] = React.useState<boolean>(false);
  const [selectedTags, setSelectedTags] = React.useState<string[]>([]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState<boolean | undefined>(
    undefined
  );
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');
  const [showRatingPrompt, setShowRatingPrompt] = React.useState(false);
  const [showPromptForm, setShowPromptForm] = React.useState(false);
  const [editingPrompt, setEditingPrompt] = React.useState<Prompt | undefined>();
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [sortPreferences, setSortPreferences] = React.useState<SortPreferences>({
    prompts: 'alpha-asc',
    folders: 'alpha-asc',
  });
  const [workFilter, setWorkFilter] = React.useState(false);
  // Guards the persist effect below from running before init has loaded the
  // stored WORK_FILTER — otherwise the mount run (state defaults to false)
  // would erase a saved "on" preference.
  const workFilterLoaded = React.useRef(false);

  // Initialize data from storage
  React.useEffect(() => {
    const initializeData = async () => {
      if (isExtension) {
        const [storedFolders, storedPrompts, storedSortPrefs, storedWorkFilter] = await Promise.all(
          [
            storage.get(STORAGE_KEYS.FOLDERS),
            storage.get(STORAGE_KEYS.PROMPTS),
            storage.get(STORAGE_KEYS.SORT_PREFERENCES),
            storage.get(STORAGE_KEYS.WORK_FILTER),
          ]
        );

        // Check if backup is needed when app is initialized
        void BackupManager.checkAndCreateBackup();

        // Ensure folders is always an array, even if storage returns null or undefined
        const foldersArray = Array.isArray(storedFolders) ? storedFolders : [];
        setFolders(foldersArray);

        // Do the same for prompts
        const promptsArray = Array.isArray(storedPrompts) ? storedPrompts : [];
        setPrompts(promptsArray);

        // Log for debugging
        console.log('STORAGE DEBUG: Loaded folders from storage:', {
          storedFolders,
          foldersArray,
          isArray: Array.isArray(storedFolders),
        });
        if (storedSortPrefs) setSortPreferences(storedSortPrefs);
        // Restore the Work filter preference before the persist effect can run.
        const initialWorkFilter = storedWorkFilter === true;
        setWorkFilter(initialWorkFilter);
        workFilterLoaded.current = true;
        setIsInitialized(true);
      }
    };

    initializeData();
  }, []);

  // Update storage when sidebar state changes
  React.useEffect(() => {
    if (isExtension && typeof isSidebarCollapsed === 'boolean') {
      storage.set(STORAGE_KEYS.SIDEBAR_COLLAPSED, isSidebarCollapsed);
    }
  }, [isSidebarCollapsed]);

  // Update storage when work filter changes. Always `set` (never `remove`):
  // the cross-view onChanged listener only reacts to `newValue !== undefined`,
  // so a `remove` wouldn't propagate a turn-off to the popup/side panel. Skip
  // until init has loaded the stored value so we don't clobber it on mount.
  React.useEffect(() => {
    if (isExtension && workFilterLoaded.current) {
      storage.set(STORAGE_KEYS.WORK_FILTER, workFilter);
    }
  }, [workFilter]);

  // Listen for storage changes
  React.useEffect(() => {
    if (isExtension) {
      const handleStorageChange = async (changes: StorageChangeMap) => {
        if (changes[STORAGE_KEYS.THEME]?.newValue !== undefined) {
          const theme = changes[STORAGE_KEYS.THEME].newValue || 'light';
          if (theme === 'dark') {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }

        if (changes[STORAGE_KEYS.SIDEBAR_COLLAPSED]?.newValue !== undefined) {
          const newValue = changes[STORAGE_KEYS.SIDEBAR_COLLAPSED].newValue;
          if (typeof newValue === 'boolean') {
            setIsSidebarCollapsed(newValue);
          }
        }

        if (changes[STORAGE_KEYS.WORK_FILTER]?.newValue !== undefined) {
          // Unwrap the {data} wrapper so a cross-view toggle propagates.
          const raw = changes[STORAGE_KEYS.WORK_FILTER].newValue;
          const newValue = raw?.data ?? raw;
          if (typeof newValue === 'boolean') {
            setWorkFilter(newValue);
          }
        }

        // Handle prompts and folders changes. Accept BOTH shapes: the UI writes
        // wrapped {data} values, but the repository layer (e.g. a popup folder
        // delete via folderService) writes raw arrays straight to storage.
        // Reading only `.data` dropped those raw-array changes, so deleted
        // folders/prompts resurrected here.
        if (changes[STORAGE_KEYS.PROMPTS]?.newValue !== undefined) {
          const raw = changes[STORAGE_KEYS.PROMPTS].newValue;
          const newPrompts = raw?.data ?? raw;
          if (Array.isArray(newPrompts)) {
            setPrompts(newPrompts);
          }
        }

        if (changes[STORAGE_KEYS.FOLDERS]?.newValue !== undefined) {
          const raw = changes[STORAGE_KEYS.FOLDERS].newValue;
          const newFolders = raw?.data ?? raw;

          // Ensure we're setting an array even if the value is not an array
          if (Array.isArray(newFolders)) {
            setFolders(newFolders);
          } else {
            console.log('STORAGE DEBUG: folders newValue is not an array:', {
              newValue: changes[STORAGE_KEYS.FOLDERS].newValue,
              newFolders,
            });

            // Keep the current folders instead of setting to an empty array
            // This prevents all folders from disappearing when one is deleted
          }
        }
      };

      return getPlatform().storage.onChanged(handleStorageChange);
    }
  }, []);

  // Check if remember last folder feature is enabled
  React.useEffect(() => {
    const loadFeatureState = async () => {
      const isEnabled = await checkFeatureEnabled('rememberLastFolder');
      setRememberLastFolderEnabled(isEnabled);
    };
    loadFeatureState();
  }, []);

  // Load last selected folder if feature is enabled
  React.useEffect(() => {
    const loadLastFolder = async () => {
      if (isExtension && rememberLastFolderEnabled) {
        const lastFolder = await storage.get(STORAGE_KEYS.LAST_SELECTED_FOLDER);
        if (lastFolder) {
          setSelectedFolderId(lastFolder);
        }
      }
    };
    loadLastFolder();
  }, [rememberLastFolderEnabled]);

  // Save last selected folder when it changes
  React.useEffect(() => {
    if (isExtension && rememberLastFolderEnabled && selectedFolderId !== null) {
      storage.set(STORAGE_KEYS.LAST_SELECTED_FOLDER, selectedFolderId);
    }
  }, [selectedFolderId, rememberLastFolderEnabled]);

  const updateStorage = React.useCallback(
    async (
      key: typeof STORAGE_KEYS.FOLDERS | typeof STORAGE_KEYS.PROMPTS,
      data: Folder[] | Prompt[]
    ) => {
      if (isExtension) {
        try {
          await storage.set(key, data);
        } catch (error) {
          console.error(`Failed to update ${key}:`, error);
        }
      }
    },
    []
  );

  // Save sort preferences when they change
  React.useEffect(() => {
    if (isExtension) {
      storage.set(STORAGE_KEYS.SORT_PREFERENCES, sortPreferences);
    }
  }, [sortPreferences]);

  const handleSortChange = (type: 'prompts' | 'folders', option: SortOption) => {
    setSortPreferences(prev => ({
      ...prev,
      [type]: option,
    }));
  };

  const handleSavePrompt = async (prompt: any): Promise<void> => {
    const validatedPrompt = {
      ...prompt,
      id: editingPrompt?.id || Date.now().toString(),
      lastChanged: Date.now(),
    };

    let newPrompts: Prompt[];
    if (editingPrompt) {
      newPrompts = prompts.map(p => (p.id === validatedPrompt.id ? validatedPrompt : p));
    } else {
      newPrompts = [...prompts, validatedPrompt];
    }
    setPrompts(newPrompts);
    updateStorage(STORAGE_KEYS.PROMPTS, newPrompts);

    // Check if backup is needed after saving a prompt
    void BackupManager.checkAndCreateBackup();

    setShowPromptForm(false);
    setEditingPrompt(undefined);
  };

  const filteredData = useMemo(() => {
    if (!workFilter) return { folders, prompts };

    const workPrompts = prompts.filter(p => p.tags.includes('Work'));
    const workFolders = folders.filter(f => workPrompts.some(p => p.folder === f.id));

    return {
      folders: workFolders,
      prompts: workPrompts,
    };
  }, [folders, prompts, workFilter]);

  const filteredPrompts = React.useMemo(() => {
    let filtered = [...filteredData.prompts];

    if (selectedFolderId) {
      const specialFolders = [SPECIAL_FOLDERS.ALL, SPECIAL_FOLDERS.UNSORTED];
      const isSpecialFolder = specialFolders.some(f => f.id === selectedFolderId);

      if (!isSpecialFolder) {
        const selectedFolder = filteredData.folders.find(f => f.id === selectedFolderId);
        filtered = filtered.filter(prompt => prompt.folder === selectedFolder?.id);
      } else if (selectedFolderId === SPECIAL_FOLDERS.UNSORTED.id) {
        filtered = filtered.filter(prompt => !prompt.folder);
      }
    }

    if (selectedTags.length > 0) {
      filtered = filtered.filter(prompt => selectedTags.every(tag => prompt.tags.includes(tag)));
    }

    filtered.sort((a, b) => {
      switch (sortPreferences.prompts) {
        case 'alpha-asc':
          return a.title.localeCompare(b.title);
        case 'alpha-desc':
          return b.title.localeCompare(a.title);
        case 'date-newest':
          return b.lastChanged - a.lastChanged;
        case 'date-oldest':
          return a.lastChanged - b.lastChanged;
        case 'usage-most':
          return 0;
        case 'usage-least':
          return 0;
        default:
          return 0;
      }
    });

    return filtered;
  }, [selectedFolderId, filteredData, selectedTags, sortPreferences.prompts]);

  const allTags = React.useMemo(() => {
    const tagSet = new Set<string>();
    prompts.forEach(prompt => prompt.tags.forEach(tag => tagSet.add(tag)));
    return Array.from(tagSet);
  }, [prompts]);

  const generateRandomColor = () =>
    '#' +
    Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, '0');

  const handleAddFolder = async (): Promise<void> => {
    const folderName = `New Folder ${folders.length + 1}`;
    const color = generateRandomColor();
    const newFolder: Folder = {
      id: Date.now().toString(),
      name: folderName,
      color,
      icon: 'Folder', // Add default icon
      isSpecial: false, // Add isSpecial property
    };
    const newFolders = [...folders, newFolder];
    setFolders(newFolders);
    setSelectedFolderId(newFolder.id);
    updateStorage(STORAGE_KEYS.FOLDERS, newFolders);
  };

  const handleAddPrompt = (): void => {
    setEditingPrompt(undefined);
    setShowPromptForm(true);
  };

  const handleEditPrompt = (prompt: Prompt): void => {
    setEditingPrompt(prompt);
    setShowPromptForm(true);
  };

  const handleDeletePrompt = async (prompt: Prompt): Promise<void> => {
    setDialogPromptId(prompt.id);
    setShowDeletePromptDialog(true);
  };

  const confirmDeletePrompt = async (): Promise<void> => {
    if (dialogPromptId) {
      try {
        // Add diagnostic logs
        console.log('DIAGNOSIS: About to get prompt service');

        // Try to initialize the infrastructure
        console.log('DIAGNOSIS: Attempting to initialize infrastructure');
        try {
          const { ServiceProvider } = await import('./infrastructure/services/serviceFactory');
          ServiceProvider.initialize();
          console.log('DIAGNOSIS: Infrastructure initialized successfully');
        } catch (initError) {
          console.error('DIAGNOSIS: Failed to initialize infrastructure:', initError);
        }

        // Get the prompt service from the infrastructure layer
        const promptService = getPromptService();
        console.log('DIAGNOSIS: Successfully got prompt service');

        // Call the domain service to handle deletion properly
        console.log('DIAGNOSIS: Calling deletePrompt with ID:', dialogPromptId);
        await promptService.deletePrompt(dialogPromptId);
        console.log('DIAGNOSIS: Successfully deleted prompt');

        // Update local state to reflect the deletion
        const newPrompts = prompts.filter(p => p.id !== dialogPromptId);
        setPrompts(newPrompts);

        // Explicitly update the storage to ensure consistent format
        console.log('DIAGNOSIS: Updating storage with filtered prompts array');
        await storage.set(STORAGE_KEYS.PROMPTS, newPrompts);
        console.log('DIAGNOSIS: Storage updated successfully');

        // Show success message
        setToastMessage('Prompt deleted');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);

        // Close dialog and reset state
        setShowDeletePromptDialog(false);
        setDialogPromptId(null);
      } catch (error) {
        console.error('Failed to delete prompt:', error);
        setToastMessage('Failed to delete prompt');
        setShowToast(true);
        setTimeout(() => setShowToast(false), 2000);
      }
    }
  };

  const handlePromptClick = async (prompt: Prompt): Promise<void> => {
    try {
      await navigator.clipboard.writeText(prompt.text);

      // Check if backup is needed after using a prompt
      void BackupManager.checkAndCreateBackup();

      void recordPromptUsage().then(shouldAsk => {
        if (shouldAsk) setShowRatingPrompt(true);
      });

      setToastMessage('Copied prompt to clipboard');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
      setToastMessage('Failed to copy prompt');
      setShowToast(true);
      setTimeout(() => setShowToast(false), 2000);
    }
  };

  return (
    <>
      <div
        className={getClasses('w-full h-screen flex overflow-hidden relative')}
        style={{
          backgroundColor: getColor('app-background'),
        }}
      >
        <Sidebar
          isFullscreen={true}
          folders={[...filteredData.folders].sort((a, b) => {
            switch (sortPreferences.folders) {
              case 'alpha-asc':
                return a.name.localeCompare(b.name);
              case 'alpha-desc':
                return b.name.localeCompare(a.name);
              // Folders have no creation timestamp and UUID ids, so
              // parseInt(id) is NaN — sort by `order` (creation/arrangement
              // sequence) instead, which is stable and never NaN (#112).
              case 'date-newest':
                return (b.order ?? 0) - (a.order ?? 0);
              case 'date-oldest':
                return (a.order ?? 0) - (b.order ?? 0);
              default:
                return 0;
            }
          })}
          prompts={filteredData.prompts}
          selectedFolderId={selectedFolderId}
          onSelectFolder={setSelectedFolderId}
          onAddFolder={handleAddFolder}
          isCollapsed={isSidebarCollapsed === undefined ? false : isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          workFilter={workFilter}
          onWorkFilterChange={setWorkFilter}
          onRenameFolder={folderId => {
            const folder = folders.find(f => f.id === folderId);
            if (folder?.isSpecial) {
              setToastMessage("Can't rename special folders");
              setShowToast(true);
              setTimeout(() => setShowToast(false), 2000);
              return;
            }
            setDialogFolderId(folderId);
            setShowRenameFolderDialog(true);
          }}
          onDeleteFolder={folderId => {
            const folder = folders.find(f => f.id === folderId);
            if (folder?.isSpecial) {
              setToastMessage("Can't delete special folders");
              setShowToast(true);
              setTimeout(() => setShowToast(false), 2000);
              return;
            }
            setDialogFolderId(folderId);
            setShowDeleteFolderDialog(true);
          }}
        />
        {!isInitialized ? (
          <div className="flex-1 w-full min-w-[300px] flex items-center justify-center">
            <div className="text-[var(--text-muted)] dark:text-[var(--text-secondary)]">
              Loading...
            </div>
          </div>
        ) : (
          <PromptList
            prompts={filteredPrompts}
            onAddPrompt={handleAddPrompt}
            onEditPrompt={handleEditPrompt}
            onSelectPrompt={handlePromptClick}
            isCollapsed={isSidebarCollapsed}
            isFullscreen={true}
            selectedTags={selectedTags}
            onSelectTag={tag => {
              setSelectedTags(prev =>
                prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
              );
            }}
            onClearTags={() => setSelectedTags([])}
            onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            onDeletePrompt={handleDeletePrompt}
            currentPromptSort={sortPreferences.prompts}
            currentFolderSort={sortPreferences.folders}
            onSortChange={handleSortChange}
          />
        )}
      </div>
      <Toast message={toastMessage} isVisible={showToast} />
      <RatingPrompt isVisible={showRatingPrompt} onClose={() => setShowRatingPrompt(false)} />

      <FolderEditDialog
        isOpen={showRenameFolderDialog}
        onClose={() => {
          setShowRenameFolderDialog(false);
          setDialogFolderId(null);
        }}
        onConfirm={(newName, newIcon, newColor) => {
          if (dialogFolderId && newName.trim()) {
            const newFolders = folders.map(folder =>
              folder.id === dialogFolderId
                ? { ...folder, name: newName.trim(), icon: newIcon, color: newColor }
                : folder
            );
            setFolders(newFolders);
            updateStorage(STORAGE_KEYS.FOLDERS, newFolders);
            setShowRenameFolderDialog(false);
            setDialogFolderId(null);
          }
        }}
        initialName={folders.find(f => f.id === dialogFolderId)?.name || ''}
        initialIcon={folders.find(f => f.id === dialogFolderId)?.icon}
        initialColor={folders.find(f => f.id === dialogFolderId)?.color}
      />

      <Dialog
        isOpen={showDeleteFolderDialog}
        onClose={() => {
          setShowDeleteFolderDialog(false);
          setDialogFolderId(null);
        }}
        onConfirm={() => {
          if (dialogFolderId) {
            // Shared live-schema deletion: removes the folder AND its prompts
            // (previously this view only removed the folder record, stranding
            // its prompts under a dead folder id). The storage change listener
            // updates local state.
            void deleteFolderWithPrompts(dialogFolderId).catch(error => {
              console.error('Error deleting folder:', error);
            });
            if (selectedFolderId === dialogFolderId) {
              setSelectedFolderId(null);
            }
            setShowDeleteFolderDialog(false);
            setDialogFolderId(null);
          }
        }}
        title="Delete Folder"
        description="Are you sure you want to delete this folder? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <Dialog
        isOpen={showDeletePromptDialog}
        onClose={() => {
          setShowDeletePromptDialog(false);
          setDialogPromptId(null);
        }}
        onConfirm={confirmDeletePrompt}
        title="Delete Prompt"
        description="Are you sure you want to delete this prompt? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
      {showPromptForm && (
        <PromptForm
          folders={folders}
          existingTags={allTags}
          onClose={() => {
            setShowPromptForm(false);
            setEditingPrompt(undefined);
          }}
          onSave={handleSavePrompt}
          initialPrompt={editingPrompt}
          currentFolderId={selectedFolderId}
          onAddFolder={async folderName => {
            const color = generateRandomColor();
            const newFolder: Folder = {
              id: crypto.randomUUID(),
              name: folderName,
              color,
              icon: 'Folder', // Add default icon
              isSpecial: false, // Add isSpecial property
            };
            const newFolders = [...folders, newFolder];
            setFolders(newFolders);
            updateStorage(STORAGE_KEYS.FOLDERS, newFolders);
            return newFolder.id;
          }}
        />
      )}
    </>
  );
}

export default FullscreenApp;
